/**
 * Reactive hooks over the extension contribution registry. Editor-shell surfaces
 * (sidebar, status bar, command palette, settings center, context menus, unit
 * popover) subscribe through these hooks to render the aggregated set of active
 * extension contributions.
 */
import type {
    AnyExtensionContract,
    ExtensionContextMenuItem,
    ExtensionMethodName,
    ExtensionMethodParams,
    ExtensionMethodResult,
    ExtensionVirtualPackageContext,
    ExtensionUIProps,
    FloatingPanelContribution,
    LocaleParams,
    MenuContext,
    SettingsPanelProps,
    SidebarPanelContribution,
    StatusBarContribution,
    UnitConfigActionContribution,
    UnitConfigContext,
} from "@magic-md/extension-sdk";
import type { FC } from "react";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import { contributionRegistry } from "./contribution-registry";
import { useExtensionHostStore } from "./extension-host-store";
import { useExtensionShellServices } from "./extension-shell-context";
import type { HostServices } from "./host-services";

/** Subscribe to contribution-registry revisions (re-render on any change). */
function useContributionRevision(): number {
    return useSyncExternalStore(
        (onChange) => contributionRegistry.subscribe(onChange),
        () => contributionRegistry.revision(),
        () => contributionRegistry.revision(),
    );
}

export interface OwnedSidebarPanel {
    extensionId: string;
    panel: SidebarPanelContribution;
}
export interface OwnedFloatingPanel {
    extensionId: string;
    panel: FloatingPanelContribution;
}
export interface OwnedStatusBarItem {
    extensionId: string;
    item: StatusBarContribution;
}
export interface OwnedUnitConfigAction {
    extensionId: string;
    action: UnitConfigActionContribution;
}
export interface OwnedVirtualPackageNode {
    extensionId: string;
    providerId: string;
    provider: ReturnType<typeof contributionRegistry.virtualPackageProviders>[number]["provider"];
    node: ReturnType<typeof contributionRegistry.buildVirtualPackages>[number]["node"];
}

/** All active extension sidebar panels. */
export function useExtensionSidebarPanels(): OwnedSidebarPanel[] {
    useContributionRevision();
    return contributionRegistry.sidebarPanels();
}

/** All active extension floating panel definitions. */
export function useExtensionFloatingPanels(): OwnedFloatingPanel[] {
    useContributionRevision();
    return contributionRegistry.floatingPanels();
}

/** Active extension status-bar items, optionally filtered by alignment. */
export function useExtensionStatusBarItems(
    align?: "left" | "center" | "right",
): OwnedStatusBarItem[] {
    useContributionRevision();
    const items = contributionRegistry.statusBarItems();
    if (!align) return items;
    return items.filter(({ item }) => (item.align ?? "left") === align);
}

/** Active unit-config actions for a given unit (or all when omitted). */
export function useExtensionUnitConfigActions(
    unit?: UnitConfigContext,
): OwnedUnitConfigAction[] {
    useContributionRevision();
    return contributionRegistry.unitConfigActions(unit);
}

export function useExtensionVirtualPackages(
    context: ExtensionVirtualPackageContext,
): OwnedVirtualPackageNode[] {
    useContributionRevision();
    const virtual = context.virtual;
    return useMemo(
        () => contributionRegistry.buildVirtualPackages(context),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            context.parentPath,
            context.parentName,
            context.parentKind,
            context.bundlePath,
            virtual?.kind,
            virtual?.physicalPath,
            virtual?.nodeName,
            virtual?.isDirectory,
            virtual?.packageName,
            virtual?.relativePath,
            virtual?.ownerDocumentPath,
            virtual?.ownerBundlePath,
            virtual?.ownerFolderPath,
            virtual?.extensionId,
            virtual?.extensionPackageId,
            virtual?.extensionNodeId,
        ],
    );
}

/** Build context-menu items contributed for a surface. */
export function useExtensionMenuItems(
    context: MenuContext,
): ExtensionContextMenuItem[] {
    useContributionRevision();
    const virtual = context.virtual;
    // contributionRegistry.buildMenu is pure over the current providers; the
    // revision dependency guarantees recomputation when providers change.
    return useMemo(
        () => contributionRegistry.buildMenu(context),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            context.surface,
            context.path,
            context.tabId,
            context.targetKind,
            context.unit?.definitionId,
            virtual?.kind,
            virtual?.physicalPath,
            virtual?.nodeName,
            virtual?.isDirectory,
            virtual?.packageName,
            virtual?.relativePath,
            virtual?.ownerDocumentPath,
            virtual?.ownerBundlePath,
            virtual?.ownerFolderPath,
        ],
    );
}

/** The settings panel component contributed by one extension, if any. */
export function useExtensionSettingsPanel(
    extensionId: string,
): FC<SettingsPanelProps> | undefined {
    useContributionRevision();
    return contributionRegistry.settingsPanel(extensionId);
}

/** Ids of extensions that contributed a settings panel. */
export function useExtensionSettingsPanelIds(): string[] {
    useContributionRevision();
    return contributionRegistry.settingsPanels().map((p) => p.extensionId);
}

function createFloatingPanelInstanceId(extensionId: string, panelId: string): string {
    return `${extensionId}:${panelId}:${Date.now().toString(36)}:${Math.random()
        .toString(36)
        .slice(2, 7)}`;
}

/**
 * Build the `ExtensionUIProps` for one extension from the mounted HostServices.
 * Returns null when no `<ExtensionShellProvider>` is mounted.
 */
export function useExtensionUIProps<
    C extends AnyExtensionContract = AnyExtensionContract,
>(extensionId: string): ExtensionUIProps<C> | null {
    const services = useExtensionShellServices();
    const openFloatingPanelInApp = useExtensionHostStore(
        (state) => state.openFloatingPanel,
    );

    const callRuntime = useCallback(
        async <K extends ExtensionMethodName<C>>(
            method: K,
            params: ExtensionMethodParams<C, K>,
        ): Promise<ExtensionMethodResult<C, K>> => {
            if (!services) throw new Error("Extension host services unavailable");
            await services.runtime.ensureActivated(
                extensionId,
                services.settings.get(extensionId),
            );
            return services.runtime.invoke<C, K>(extensionId, method, params);
        },
        [services, extensionId],
    );

    const onRuntimeEvent = useCallback(
        (event: string, handler: (data: unknown) => void) => {
            if (!services) return () => {};
            const subscribe = services.runtime.subscribe as unknown as (
                id: string,
                ev: string,
                cb: (data: unknown) => void,
            ) => () => void;
            return subscribe(extensionId, event, handler);
        },
        [services, extensionId],
    );

    const openFloatingPanel = useCallback<
        NonNullable<ExtensionUIProps<C>["openFloatingPanel"]>
    >(
        (panelId, options) => {
            const viewportWidth = typeof window === "undefined" ? 1280 : window.innerWidth;
            const viewportHeight = typeof window === "undefined" ? 800 : window.innerHeight;
            const width = options?.width ?? 460;
            const height = options?.height ?? 520;
            const instanceId = createFloatingPanelInstanceId(extensionId, panelId);
            return openFloatingPanelInApp({
                instanceId,
                extensionId,
                panelId,
                title: options?.title,
                payload: options?.payload,
                x:
                    options?.x ??
                    Math.max(56, Math.round((viewportWidth - width) / 2)),
                y:
                    options?.y ??
                    Math.max(48, Math.round((viewportHeight - height) / 3)),
                width,
                height,
            });
        },
        [extensionId, openFloatingPanelInApp],
    );

    return useMemo(() => {
        if (!services) return null;
        const namespace = extensionId;
        return {
            extensionId,
            settings: services.settings.get(extensionId),
            callRuntime,
            onRuntimeEvent: onRuntimeEvent as ExtensionUIProps<C>["onRuntimeEvent"],
            host: services.host,
            t: (key: string, params?: LocaleParams) =>
                services.i18n.translate(namespace, key, params),
            locale: services.i18n.locale,
            openFloatingPanel,
        } satisfies ExtensionUIProps<C>;
    }, [services, extensionId, callRuntime, onRuntimeEvent, openFloatingPanel]);
}

/** Returns the host's scoped translator for a status-bar/render callback. */
export function makeExtensionTranslator(
    services: HostServices | null,
    extensionId: string,
): (key: string, params?: LocaleParams) => string {
    if (!services) return (key) => key;
    return (key, params) => services.i18n.translate(extensionId, key, params);
}
